// File: frontend/src/components/marketing/visuals/primitives.js
//
// Shared presentational primitives for the marketing product visuals. All are
// theme-aware (light/dark) via the MUI theme, contain no business logic, and
// make no network requests. Reduced-motion is respected by the count-up hook and
// by keeping decorative motion opt-in at the call site.

import React from "react";
import { Box, Paper, Typography, Stack, useTheme, alpha } from "@mui/material";
import { motion } from "framer-motion";
import { useCountUp } from "../motion";

const MoBox = motion(Box);

// ── Browser-style frame ──────────────────────────────────────────────────────
// `url` is decoration, but it must be a real address: pesafii.net and its
// actual routes, never an invented app host.
export function MarketingBrowserFrame({ children, url = "pesafii.net", accentGlow = true, sx }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const green = theme.palette.primary.main;
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3, overflow: "hidden", border: "1px solid", borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: dark
          ? `0 30px 70px rgba(0,0,0,0.55)${accentGlow ? `, 0 0 0 1px ${alpha(green, 0.08)}` : ""}`
          : `0 30px 70px rgba(15,23,42,0.16)${accentGlow ? `, 0 0 0 1px ${alpha(green, 0.06)}` : ""}`,
        width: "100%", minWidth: 0,
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}>
        <Stack direction="row" spacing={0.75}>
          {["#ef4444", "#f59e0b", "#22c55e"].map((c) => (
            <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: alpha(c, 0.85) }} />
          ))}
        </Stack>
        <Box sx={{ flex: 1, mx: 1, py: 0.4, px: 1.25, borderRadius: 1.5, bgcolor: "background.default", border: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: green }} />
          <Typography sx={{ fontSize: 11, color: "text.disabled" }}>{url}</Typography>
        </Box>
      </Box>
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>{children}</Box>
    </Paper>
  );
}

// ── Generic device (phone) frame ─────────────────────────────────────────────
export function MarketingMobileFrame({ children, sx }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  return (
    <Box
      sx={{
        width: 260, maxWidth: "100%", mx: "auto",
        borderRadius: 6, p: 1, bgcolor: dark ? "#05090f" : "#0B1622",
        border: "1px solid", borderColor: dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.2)",
        boxShadow: dark ? "0 30px 60px rgba(0,0,0,0.5)" : "0 30px 60px rgba(15,23,42,0.22)",
        ...sx,
      }}
    >
      {/* notch */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 0.5 }}>
        <Box sx={{ width: 70, height: 5, borderRadius: 99, bgcolor: "rgba(255,255,255,0.25)" }} />
      </Box>
      <Box sx={{ borderRadius: 4.5, overflow: "hidden", bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
        {children}
      </Box>
    </Box>
  );
}

// ── Status badge (semantic, theme-aware) ─────────────────────────────────────
const TONE = {
  online: "success", available: "success", completed: "success", success: "success",
  redeemed: "info", info: "info",
  processing: "warning", pending: "warning", checking: "warning",
  offline: "neutral", expired: "error", failed: "error",
};
const LABEL = {
  online: "Online", offline: "Offline", available: "Available", redeemed: "Redeemed",
  expired: "Expired", processing: "Processing", completed: "Completed", pending: "Pending",
};

export function MarketingStatusBadge({ status, label, size = "sm" }) {
  const theme = useTheme();
  const tone = TONE[status] || "neutral";
  const color = tone === "neutral" ? theme.palette.text.secondary : theme.palette[tone].main;
  const font = size === "sm" ? 10.5 : 11.5;
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5, px: 0.9, py: 0.25, borderRadius: 99,
      bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.16 : 0.12),
      border: `1px solid ${alpha(color, 0.3)}`, whiteSpace: "nowrap",
    }}>
      <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: color }} />
      <Typography component="span" sx={{ fontSize: font, fontWeight: 700, color, lineHeight: 1.4 }}>
        {label || LABEL[status] || status}
      </Typography>
    </Box>
  );
}

// ── Metric card with count-up + optional accent bar ──────────────────────────
export function MarketingMetricCard({ label, value, prefix = "", suffix = "", icon: Icon, color, active = true, decimals = 0, sx }) {
  const theme = useTheme();
  const accent = color || theme.palette.primary.main;
  const n = useCountUp(value, { active });
  const shown = decimals ? n.toFixed(decimals) : Math.round(n).toLocaleString();
  return (
    <Paper sx={{
      p: 1.5, borderRadius: 2.5, border: "1px solid", borderColor: "divider", position: "relative", overflow: "hidden", minWidth: 0, height: "100%",
      ...sx,
    }}>
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, bgcolor: accent }} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        {Icon && (
          <Box sx={{ width: 26, height: 26, borderRadius: 1.5, flexShrink: 0, bgcolor: alpha(accent, 0.14), color: accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon sx={{ fontSize: 15 }} />
          </Box>
        )}
        <Typography sx={{ fontSize: 10.5, color: "text.secondary", fontWeight: 600, letterSpacing: "0.03em", lineHeight: 1.3, minWidth: 0 }}>{label}</Typography>
      </Stack>
      <Typography sx={{ fontSize: { xs: 13, sm: 17 }, fontWeight: 800, color: "text.primary", lineHeight: 1.1, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        {prefix}{shown}{suffix}
      </Typography>
    </Paper>
  );
}

// ── Mini bar chart (SVG, draws in) ───────────────────────────────────────────
export function MarketingMiniBars({ data = [], color, height = 74, active = true, animate = true }) {
  const theme = useTheme();
  const c = color || theme.palette.primary.main;
  const max = Math.max(...data, 1);
  return (
    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.75, height }}>
      {data.map((v, i) => (
        <MoBox
          key={i}
          initial={animate ? { height: 0 } : false}
          animate={active ? { height: `${(v / max) * 100}%` } : { height: 0 }}
          transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
          sx={{ flex: 1, borderRadius: "3px 3px 0 0", bgcolor: i === data.length - 1 ? c : alpha(c, 0.4), minWidth: 0 }}
        />
      ))}
    </Box>
  );
}

// ── Mini sparkline / line chart (SVG path draw) ──────────────────────────────
export function MarketingMiniLine({ data = [], color, height = 90, active = true }) {
  const theme = useTheme();
  const c = color || theme.palette.primary.main;
  if (!data.length) return null;
  const w = 300;
  const max = Math.max(...data), min = Math.min(...data), range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = height - ((v - min) / range) * (height - 12) - 6;
    return [x, y];
  });
  const line = pts.map((p) => p.join(",")).join(" ");
  const area = `0,${height} ${line} ${w},${height}`;
  const gid = `mkl-${c.replace("#", "")}`;
  return (
    <Box sx={{ width: "100%" }}>
      <svg viewBox={`0 0 ${w} ${height}`} style={{ width: "100%", height }} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c} stopOpacity="0.28" />
            <stop offset="100%" stopColor={c} stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.polygon points={area} fill={`url(#${gid})`}
          initial={{ opacity: 0 }} animate={active ? { opacity: 1 } : { opacity: 0 }} transition={{ duration: 0.6, delay: 0.5 }} />
        <motion.polyline points={line} fill="none" stroke={c} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"
          initial={{ pathLength: 0 }} animate={active ? { pathLength: 1 } : { pathLength: 0 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} />
        {pts.map(([x, y], i) => (
          <motion.circle key={i} cx={x} cy={y} r={i === pts.length - 1 ? 4 : 2.5} fill={c}
            initial={{ opacity: 0, scale: 0 }} animate={active ? { opacity: i === pts.length - 1 ? 1 : 0.5, scale: 1 } : { opacity: 0 }}
            transition={{ delay: 0.4 + i * 0.08 }} />
        ))}
      </svg>
    </Box>
  );
}

// ── Small labelled stat row (used in tables/feeds) ───────────────────────────
export function SampleNote({ children = "Illustrative sample data — not a live account", sx }) {
  return (
    <Typography sx={{ fontSize: 9.5, color: "text.disabled", textAlign: "center", mt: 1, ...sx }}>
      {children}
    </Typography>
  );
}
